import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    IdCard, ShieldCheck, FileText, Briefcase, Bookmark, Calendar, Video, Eye,
    Sparkles, ArrowRight, Loader2, MessageSquare, Zap, BadgeCheck, AlertTriangle,
    RotateCcw,
} from "lucide-react";
import { StatCard } from "@/components/dashboard/DashboardLayout";
import { OnboardingChecklist } from "@/components/dashboard/OnboardingChecklist";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import TalentRegionGate from "@/components/talent/TalentRegionGate";
import ProfileStrengthCard from "@/components/talent/ProfileStrengthCard";
import CoachStreakCard from "@/components/talent/CoachStreakCard";
import TalentFitPreview from "@/components/talent/TalentFitPreview";

const TalentDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [scoring, setScoring] = useState(false);
    const [idvStarting, setIdvStarting] = useState(false);
    // Undo-banner state: after a talent soft-deletes their passport we
    // give them 24h to reverse it. The dashboard `/professional/dashboard`
    // response includes `pending_undo` while the grace window is open.
    const [undoBusy, setUndoBusy] = useState(false);
    // Ticks every 30s while the undo banner is mounted so the "Xh Ym left"
    // countdown feels alive without hammering renders. Stored as an int
    // (ms since epoch) so React's === bail-out still short-circuits when
    // nothing else changed.
    const [nowMs, setNowMs] = useState(() => Date.now());
    // iter 149.2 — auto-open the "Start now" modal exactly once per fresh
    // employer request. `idvDetail` holds the /talent/id-verification/status
    // payload (requested_at, requested_by) so the modal can name the
    // employer + we can dedupe by `requested_at` in localStorage.
    const [idvDetail, setIdvDetail] = useState(null);
    const [idvModal, setIdvModal] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/professional/dashboard");
            setStats(data);
            return data;
        } catch (err) {
            toast.error(formatApiError(err));
            return null;
        }
    }, []);

    useEffect(() => {
        (async () => {
            const data = await load();
            if (data?.has_passport && data.resume_score == null) {
                setScoring(true);
                try { await api.post("/professional/resume-score"); await load(); }
                catch (err) {
                    // Resume scoring is a background convenience — next page
                    // visit will retry. No user-facing toast (they didn't ask
                    // for scoring; it's automatic).
                    console.warn("[TalentDashboard] resume-score kickoff failed:", err?.message || err);
                }
                finally { setScoring(false); }
            }
        })();
    }, [load]);

    // If we returned from Stripe Identity, poll for a few seconds so the tile
    // flips to Verified without a manual refresh.
    useEffect(() => {
        const url = new URL(window.location.href);
        if (url.searchParams.get("verification") !== "done") return;
        let tries = 0;
        const t = setInterval(async () => {
            tries += 1;
            const data = await load();
            if (data?.id_verified || tries >= 8) {
                clearInterval(t);
                if (data?.id_verified) toast.success("Your identity is verified — the Verified badge is active.");
            }
        }, 2500);
        return () => clearInterval(t);
    }, [load]);

    // iter 149.2 — fetch full ID-verification status (requester label +
    // requested_at) whenever the dashboard flags a pending employer
    // request. Auto-open the "Start now" modal ONCE per unique
    // `requested_at` value — once dismissed, the modal never re-opens
    // for that same request, but a new request from another employer
    // (which mutates requested_at) re-triggers it.
    useEffect(() => {
        if (!stats?.id_verification_requested || stats?.id_verified) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/talent/id-verification/status");
                if (cancelled) return;
                setIdvDetail(data);
                const key = data?.requested_at
                    ? `simonara.idv.autopop.${data.requested_at}`
                    : null;
                if (key && !localStorage.getItem(key)) {
                    setIdvModal(true);
                    try { localStorage.setItem(key, "1"); } catch { /* quota — accept re-open on next visit */ }
                }
            } catch { /* non-fatal — card CTA still works */ }
        })();
        return () => { cancelled = true; };
    }, [stats?.id_verification_requested, stats?.id_verified]);

    const startIdVerification = async () => {
        try {
            setIdvStarting(true);
            const { data } = await api.post("/talent/id-verification/start");
            if (data?.url) {
                window.location.href = data.url;
            } else {
                toast.error("Could not open verification.");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setIdvStarting(false);
        }
    };

    const undoPassportDelete = async () => {
        try {
            setUndoBusy(true);
            await api.post("/professional/passport/undelete");
            toast.success("Your passport is back. Nothing was lost.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUndoBusy(false);
        }
    };

    // Keep the countdown ticking while pending_undo is active. 30-second
    // cadence is enough for a "23h 42m left" style readout; we auto-reload
    // the dashboard payload when we cross into the last hour or the grace
    // window has actually elapsed so the banner flips to expired /
    // disappears once the sweep cron runs.
    const hardAt = stats?.pending_undo?.hard_delete_at
        ? Date.parse(stats.pending_undo.hard_delete_at)
        : null;
    useEffect(() => {
        if (!hardAt) return undefined;
        const tick = () => setNowMs(Date.now());
        tick();
        const iv = setInterval(tick, 30 * 1000);
        return () => clearInterval(iv);
    }, [hardAt]);

    // Auto-refetch the dashboard payload the moment the grace window is
    // (approximately) up — the row is still there until the hourly sweep
    // hard-deletes it, but the banner shouldn't offer a Restore button
    // that the server will reject with 410.
    useEffect(() => {
        if (!hardAt) return undefined;
        const msLeft = hardAt - Date.now();
        if (msLeft <= 0) return undefined;
        const t = setTimeout(() => { load(); }, msLeft + 1000);
        return () => clearTimeout(t);
    }, [hardAt, load]);

    const formatCountdown = (target) => {
        const ms = target - nowMs;
        if (ms <= 0) return "expired";
        const totalSec = Math.floor(ms / 1000);
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const s = totalSec % 60;
        if (h > 0) return `${h}h ${m}m left`;
        if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s left`;
        return `${s}s left`;
    };

    if (stats === null) {
        return <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-emerald-500" /></div>;
    }

    const firstName = (stats.name || user?.name || "").split(" ")[0];

    return (
        <div data-testid="talent-dashboard">
            <div className="mb-4"><TalentRegionGate /></div>
            <div className="mb-4"><ProfileStrengthCard /></div>
            {/* iter-171 — Talent-side Fit Preview tile. Reuses the employer
                factor engine so there's one source of truth. */}
            <div className="mb-6"><TalentFitPreview /></div>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <p className="overline">Your AI Career Dashboard</p>
                <h1 className="mt-2 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">
                    {stats.has_passport ? "Welcome back" : "Welcome"}{firstName ? `, ${firstName}` : ""} <span className="inline-block">👋</span>
                </h1>
                <p className="mt-2 text-muted-foreground max-w-2xl">
                    {stats.has_passport
                        ? "Your Talent Passport is live. Simonara AI keeps working for you around the clock."
                        : "You're one step away — build your Talent Passport to unlock your full dashboard."}
                </p>
            </motion.div>

            {/* 24h undo banner — surfaces when the user soft-deleted their
                passport recently. Gives them a one-click restore path so
                the safety net actually lands in front of them instead of
                being hidden behind an API. Disappears once the row is
                either restored (undelete) or purged by the hourly sweep. */}
            {stats.pending_undo && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="passport-undo-banner"
                    className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 sm:p-5 flex items-start gap-3 flex-wrap"
                >
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-700 dark:text-amber-400 flex items-center justify-center shrink-0">
                        <AlertTriangle size={18} />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-heading font-semibold text-sm text-foreground">
                                You just deleted your passport
                            </h3>
                            {hardAt && (
                                <span
                                    data-testid="passport-undo-countdown"
                                    className="inline-flex items-center gap-1 text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full bg-amber-500/25 text-amber-800 dark:text-amber-200 tabular-nums"
                                    title={new Date(hardAt).toLocaleString()}
                                >
                                    {formatCountdown(hardAt)}
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            Restore it in one click before the deadline —
                            after that it&apos;s permanently purged.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={undoPassportDelete}
                        disabled={undoBusy || (hardAt && nowMs >= hardAt)}
                        data-testid="passport-undo-restore"
                        className="text-xs px-3 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60 inline-flex items-center gap-1.5 shrink-0"
                    >
                        {undoBusy
                            ? <><Loader2 size={13} className="animate-spin" /> Restoring…</>
                            : <><RotateCcw size={13} /> Restore my passport</>}
                    </button>
                </motion.div>
            )}

            {!stats.has_passport && !stats.pending_undo && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 card-soft p-6 sm:p-8 max-w-2xl">
                    <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                            <IdCard size={20} />
                        </div>
                        <div className="flex-1">
                            <h2 className="font-heading font-semibold text-xl text-foreground">Build your Talent Passport™</h2>
                            <p className="mt-1.5 text-sm text-muted-foreground">Upload your resume once and Simonara AI will build your verified profile — making you discoverable by employers on the platform.</p>
                            <Link to="/professional/passport" data-testid="dashboard-start-passport" className="btn-green mt-4 inline-flex text-sm">
                                Start your Passport <ArrowRight size={14} className="ml-2" />
                            </Link>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Onboarding checklist */}
            <div className="mt-8">
                <OnboardingChecklist endpoint="/professional/onboarding" ctaTo="/professional/passport" ctaLabel="Complete your Passport" testId="talent-onboarding" />
            </div>

            {/* Monthly AI allowance chips — Free = 3/3/3/3 across tools */}
            {stats.ai_usage && (
                <div data-testid="talent-usage-chips" className="mt-6 card-soft p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                        <div>
                            <h3 className="font-heading font-semibold text-sm text-foreground">This month's AI allowance</h3>
                            <p className="text-xs text-muted-foreground">Free plan · resets on the 1st of each month</p>
                        </div>
                        <Link to="/talent/billing" className="text-xs text-emerald-600 hover:underline font-medium">
                            Upgrade to Pro →
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        {[
                            ["ai_resume_optimize", "Resume optimization"],
                            ["ai_cover_letter", "Cover letters"],
                            ["ai_interview_turn", "Interview practice"],
                            ["ai_job_match", "AI job matches"],
                        ].map(([key, label]) => {
                            const u = stats.ai_usage[key] || {};
                            const left = u.unlimited ? "∞" : `${Math.max((u.limit || 0) - (u.used || 0), 0)}`;
                            const total = u.unlimited ? "Unlimited" : `left of ${u.limit || 0} this month`;
                            const empty = !u.unlimited && (u.remaining ?? 0) <= 0;
                            return (
                                <div key={key} data-testid={`usage-chip-${key}`} className={`rounded-xl border p-3 ${empty ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-muted/20"}`}>
                                    <div className="text-muted-foreground">{label}</div>
                                    <div className="mt-0.5 font-heading text-lg text-foreground">
                                        <span className={empty ? "text-amber-600" : "mono-score"}>{left}</span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">{total}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Level 2 ID Verification — Stripe Identity ($1.50, employer-requested only, iter 148) */}
            <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                data-testid="id-verification-card"
                className={`mt-6 card-soft p-6 flex items-start gap-4 flex-wrap ${stats.id_verified
                        ? "bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-500/10 dark:to-transparent border-emerald-500/40"
                        : ""}`}
            >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${stats.id_verified
                        ? "bg-emerald-500 text-white"
                        : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`}>
                    {stats.id_verified ? <BadgeCheck size={22} /> : <ShieldCheck size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-heading font-semibold text-lg text-foreground">
                            {stats.id_verified
                                ? "Verified Talent Passport™ — active"
                                : stats.id_verification_pending
                                    ? "ID verification in review"
                                    : stats.id_verification_requested
                                        ? "Identity verification requested"
                                        : "Identity verification"}
                        </h2>
                        {stats.id_verified && (
                            <span data-testid="verified-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                                <BadgeCheck size={11} /> Verified
                            </span>
                        )}
                    </div>
                    <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
                        {stats.id_verified
                            ? "Your government ID and selfie were confirmed. Employers see a green Verified badge on your Passport."
                            : stats.id_verification_pending
                                ? "We're processing your verification. This usually completes within a few minutes — you'll see the badge here as soon as it's approved."
                                : stats.id_verification_requested
                                    ? "An employer has requested a government-ID + selfie check for your profile. It takes about two minutes and only needs to be done once."
                                    : "Identity verification becomes available when an employer requests it for your profile. You'll get a notification here — nothing to do until then."}
                    </p>
                    {!stats.id_verified && stats.id_verification_requested && (
                        <button
                            onClick={startIdVerification}
                            disabled={idvStarting}
                            data-testid="id-verification-cta"
                            className="btn-green mt-4 inline-flex items-center text-sm disabled:opacity-60"
                        >
                            {idvStarting
                                ? <><Loader2 size={14} className="animate-spin mr-2" /> Opening secure flow…</>
                                : <>{stats.id_verification_pending ? "Retry verification" : "Start verification"} <ArrowRight size={14} className="ml-2" /></>}
                        </button>
                    )}
                </div>
            </motion.div>

            {/* 8 stat cards */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <StatCard testId="stat-passport" to="/professional/passport"
                    icon={IdCard} label="Talent Passport" value={`${stats.passport_completion}%`}
                    sub="Profile completeness" accent="green" />
                <StatCard testId="stat-trust"
                    icon={ShieldCheck} label="Verifications" value={stats.trust_score}
                    sub={stats.id_verified ? "Identity verified · add more checks" : "Grows when an employer verifies your ID"} accent="blue" />
                <StatCard testId="stat-resume"
                    icon={FileText} label="Resume Score"
                    value={scoring ? "…" : (stats.resume_score != null ? `${stats.resume_score}/100` : "—")}
                    sub={stats.resume_feedback || "AI-scored recruiter impact"} accent="violet" />
                <StatCard testId="stat-matches" to="/professional/matches"
                    icon={Briefcase} label="Jobs Matching You" value={stats.matching_jobs}
                    sub="Curated from Simonara employers" accent="green" />
                <StatCard testId="stat-applications" to="/professional/applications"
                    icon={FileText} label="Applications" value={stats.applications}
                    sub="Track every conversation" accent="slate" />
                <StatCard testId="stat-saved" to="/professional/matches"
                    icon={Bookmark} label="Saved Jobs" value={stats.saved_jobs}
                    sub="Roles you're watching" accent="amber" />
                <StatCard testId="stat-interviews"
                    icon={Calendar} label="Interviews" value={stats.interviews}
                    sub={stats.offers > 0 ? `${stats.offers} offer${stats.offers === 1 ? "" : "s"} received` : "None invited yet"} accent="pink" />
                <StatCard testId="stat-visibility"
                    icon={Eye} label="Employer Visibility" value={stats.employer_visibility}
                    sub="Based on passport completeness" accent="violet" />
            </div>

            {/* Quick actions */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="rounded-2xl bg-brand-navy dark:bg-slate-900 text-white p-7 relative overflow-hidden">
                    <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-500/20 blur-3xl" />
                    <div className="relative">
                        <div className="flex items-center gap-2">
                            <Zap size={16} className="text-emerald-400" />
                            <p className="overline !text-brand-green">AI Career Coach</p>
                        </div>
                        <h3 className="mt-2 font-heading font-semibold text-xl">Practice interviews. Prepare with confidence.</h3>
                        <p className="mt-1.5 text-sm text-white/70 max-w-md">Behavioral drills, cover letters, and salary negotiation — always on.</p>
                        <Link to="/professional/interview-coach" data-testid="dash-cta-coach" className="btn-green mt-5 inline-flex text-sm">
                            Open Career Coach <ArrowRight size={14} className="ml-2" />
                        </Link>
                    </div>
                </div>
                <div className="card-soft p-7">
                    <Video size={18} className="text-emerald-500" />
                    <h3 className="mt-3 font-heading font-semibold text-lg text-foreground">Record a 60s Video Introduction</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground">Stand out with a short verified intro — employers love it. Coming soon.</p>
                    <span data-testid="dash-video-soon" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
                        Coming Soon
                    </span>
                </div>
            </div>

            {/* iter 143 — Simonara Coach thin first slice. Three free tools,
                every one ends with a deep-link CTA back to the Talent
                Passport so the coach funnels into profile completion. */}
            <div className="mt-10" data-testid="dashboard-coach-section">
                <div className="flex items-end justify-between gap-2 flex-wrap mb-4">
                    <div>
                        <p className="overline">Career Coach</p>
                        <h2 className="mt-1 font-heading font-semibold text-xl text-foreground">Free AI tools for your Passport</h2>
                        <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                            Sharpen your resume, practice interviews, and prepare for salary
                            conversations. Everything you learn can be added straight to your
                            Talent Passport.
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* iter 144 — streak card sits first so the user sees
                        their weekly progress before the tool CTAs. */}
                    <CoachStreakCard />
                    <Link
                        to="/professional/coach-tools?tool=resume"
                        data-testid="dash-coach-resume"
                        className="card-soft p-5 hover:border-emerald-500/40 transition group"
                    >
                        <FileText size={18} className="text-emerald-500" />
                        <h3 className="mt-3 font-heading font-semibold text-foreground">Resume feedback</h3>
                        <p className="mt-1 text-xs text-muted-foreground">Paste your resume — get plain-language feedback and bullet rewrites.</p>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 group-hover:underline">
                            Open <ArrowRight size={12} />
                        </span>
                    </Link>
                    <Link
                        to="/professional/coach-tools?tool=interview"
                        data-testid="dash-coach-interview"
                        className="card-soft p-5 hover:border-emerald-500/40 transition group"
                    >
                        <MessageSquare size={18} className="text-emerald-500" />
                        <h3 className="mt-3 font-heading font-semibold text-foreground">Interview practice</h3>
                        <p className="mt-1 text-xs text-muted-foreground">One question at a time. Per-answer feedback, then a wrap-up.</p>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 group-hover:underline">
                            Start <ArrowRight size={12} />
                        </span>
                    </Link>
                    <Link
                        to="/professional/coach-tools?tool=salary"
                        data-testid="dash-coach-salary"
                        className="card-soft p-5 hover:border-emerald-500/40 transition group"
                    >
                        <Sparkles size={18} className="text-emerald-500" />
                        <h3 className="mt-3 font-heading font-semibold text-foreground">Salary negotiation</h3>
                        <p className="mt-1 text-xs text-muted-foreground">Framework, scripts, and where to research the range — no invented numbers.</p>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 group-hover:underline">
                            Learn <ArrowRight size={12} />
                        </span>
                    </Link>
                </div>
            </div>

            {/* Bottom hint */}
            <div className="mt-8 card-soft p-6 flex items-start gap-3">
                <Sparkles size={18} className="text-emerald-500 shrink-0 mt-0.5" />
                <div className="text-sm">
                    <span className="font-semibold text-foreground">Tip:</span>{" "}
                    <span className="text-muted-foreground">
                        Use <kbd className="px-1.5 py-0.5 rounded bg-muted border border-border text-[10px]">⌘K</kbd> anywhere on Simonara to jump between pages.{" "}
                        <Link to="/inbox" className="text-brand-green hover:underline"><MessageSquare size={12} className="inline mr-0.5" /> Check your inbox</Link>{" "}
                        for interview requests.
                    </span>
                </div>
            </div>

            {/* iter 149.2 — Auto-opening "Start now" modal for fresh ID request */}
            {idvModal && idvDetail && !stats.id_verified && (
                <div
                    className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
                    onClick={() => setIdvModal(false)}
                    data-testid="idv-startnow-modal-backdrop"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        className="bg-background rounded-2xl max-w-md w-full p-6 shadow-2xl border border-border"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="idv-startnow-modal"
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
                                <ShieldCheck size={22} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="font-heading font-semibold text-lg text-foreground">
                                    Quick ID check
                                </h2>
                                <p className="mt-1.5 text-sm text-muted-foreground">
                                    <strong className="text-foreground">{idvDetail.requested_by || "An employer"}</strong> just asked for a government-ID + selfie check on your Talent Passport. It takes about 60 seconds and unlocks the green Verified badge on your profile.
                                </p>
                                <ul className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                                    <li className="flex items-start gap-1.5"><BadgeCheck size={12} className="text-emerald-500 mt-0.5 shrink-0" /> Simonara covers the cost — you're not billed.</li>
                                    <li className="flex items-start gap-1.5"><BadgeCheck size={12} className="text-emerald-500 mt-0.5 shrink-0" /> Runs in a secure Stripe-hosted window.</li>
                                    <li className="flex items-start gap-1.5"><BadgeCheck size={12} className="text-emerald-500 mt-0.5 shrink-0" /> One-time — good for every future employer on Simonara.</li>
                                </ul>
                            </div>
                        </div>
                        <div className="mt-5 flex items-center justify-end gap-2">
                            <button
                                onClick={() => setIdvModal(false)}
                                className="btn-outline text-sm px-4 py-2"
                                data-testid="idv-startnow-later"
                            >
                                Later
                            </button>
                            <button
                                onClick={async () => { setIdvModal(false); await startIdVerification(); }}
                                disabled={idvStarting}
                                className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-60"
                                data-testid="idv-startnow-cta"
                            >
                                {idvStarting
                                    ? <><Loader2 size={13} className="animate-spin" /> Opening…</>
                                    : <>Start verification <ArrowRight size={13} /></>}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default TalentDashboard;
